/**
 * @file Labs frontend API owner.
 */

import {
  lookupLabsPatientCandidatesCallable as configuredLookupLabsPatientCandidatesCallable,
  saveLabItemCallable as configuredSaveLabItemCallable,
  updateLabPatientLinkCallable as configuredUpdateLabPatientLinkCallable,
} from "../../app/firebase/callables.js";

/**
 * Returns the module permission required for Labs workflows.
 * @returns {string} Labs permission key.
 */
export function getLabsPermissionKey() {
  return "labs";
}

/**
 * Returns whether Labs runtime workflows are exposed.
 * @returns {boolean} True when Labs form/API workflows are available.
 */
export function isLabsRuntimeEnabled() {
  return true;
}

/**
 * Creates Labs API helpers.
 * @param {object} deps Dependency bag.
 * @returns {object} Labs API helpers.
 */
export function createLabsApi({
  lookupLabsPatientCandidatesCallable,
  saveLabItemCallable,
  updateLabPatientLinkCallable,
}) {
  return {
    /**
     * Looks up active/open Patient candidates through the approved Labs link
     * workflow.
     * @param {object} params Lookup params.
     * @param {string} [params.search] Search text.
     * @param {Array<string>} [params.typeIds] Optional Patient type IDs.
     * @param {number} [params.limit] Result limit.
     * @returns {Promise<object>} Callable result payload.
     */
    async lookupLabsPatientCandidates({
      search = "",
      typeIds = [],
      limit = 20,
    } = {}) {
      const result = await lookupLabsPatientCandidatesCallable({
        search,
        typeIds,
        limit,
      });
      return result.data;
    },

    /**
     * Saves a Lab item through backend validation.
     * @param {object} params Save params.
     * @param {string|null} params.itemId Existing item id for updates.
     * @param {object} params.data Submitted Lab data.
     * @returns {Promise<object>} Callable result payload.
     */
    async saveLabItem({itemId = null, data = {}} = {}) {
      const result = await saveLabItemCallable({itemId, data});
      return result.data;
    },

    /**
     * Updates a Lab item's linked Patient through backend authority.
     * @param {object} params Link params.
     * @param {string} params.labId Lab item id.
     * @param {string|null} params.patientId Target Patient id, or null.
     * @param {*} [params.expectedUpdatedAt=null] Expected item update time.
     * @param {string|null} [params.idempotencyKey=null] Optional retry key.
     * @returns {Promise<object>} Callable result payload.
     */
    async updateLabPatientLink({
      labId,
      patientId = null,
      expectedUpdatedAt = null,
      idempotencyKey = null,
    } = {}) {
      const result = await updateLabPatientLinkCallable({
        labId,
        patientId,
        expectedUpdatedAt,
        idempotencyKey,
      });
      return result.data;
    },
  };
}

const configuredLabsApi = createLabsApi({
  lookupLabsPatientCandidatesCallable:
    configuredLookupLabsPatientCandidatesCallable,
  saveLabItemCallable: configuredSaveLabItemCallable,
  updateLabPatientLinkCallable: configuredUpdateLabPatientLinkCallable,
});

export const lookupLabsPatientCandidates =
  configuredLabsApi.lookupLabsPatientCandidates;
export const saveLabItem = configuredLabsApi.saveLabItem;
export const updateLabPatientLink = configuredLabsApi.updateLabPatientLink;
